/**
 * Module dependencies.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Button } from 'src/components/core/buttons/button';
import { Container } from 'src/components/core/layout/container';
import { District, getRadarDistricts } from 'src/api/get-radar-districts';
import { ErrorMessage, SuccessMessage } from 'src/components/schedule/message';
import { ExitConfirmationModal } from 'src/components/schedule/exit-confirmation-modal';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { GetStaticProps, NextPage } from 'next';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { Content as PrismicContent } from '@prismicio/client';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { ScheduleProvider, useSchedule } from 'src/providers/schedule';
import { ServicePayment } from 'src/components/schedule/service-payment';
import { Steps } from 'src/components/schedule/steps';
import { StepsProgress } from 'src/components/schedule/steps-progress';
import { Svg } from 'src/components/core/svg';
import { createPrimsicClient } from 'src/prismic/client';
import { media } from '@untile/react-core/styles/media';
import { normalizePrismicSEO } from 'src/prismic/utils';
import { routes } from 'src/core/constants/routes';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'src/providers/history';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { withSettings } from 'src/hocs/with-settings';
import closeSvg from 'src/assets/svgs/close.svg';
import logoSvg from 'src/assets/svgs/logo.svg';
import scheduleSvg from 'src/assets/svgs/schedule.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: PrismicContent.HomeDocumentData;
  districts: {
    data: District[] | null;
    status: number;
  };
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  color: var(--color-text-secondary);
  padding: 0 var(--gutter) 72px;
`;

/**
 * `Nav` styled component.
 */

const Nav = styled.nav`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 72px;
  padding: 18px 0;

  ${media.max.md`
    padding: 18px var(--space-sm);
  `}
`;

/**
 * `StyledHeading` styled component.
 */

const StyledHeading = styled(Heading)`
  margin-bottom: 42px;
  scroll-margin-top: 14px;

  ${media.max.md`
    padding: 0 var(--space-sm);
  `}
`;

/**
 * `StepsGrid` styled component.
 */

const StepsGrid = styled.div`
  display: grid;
  grid-column-gap: var(--section-space);
  grid-row-gap: 0;
  grid-template-columns: calc(40% - (var(--section-space) / 2)) calc(60% - (var(--section-space) / 2));

  ${media.max.md`
    grid-row-gap: var(--space-xs);
    grid-template-columns: 100%;
  `}
`;

/**
 * `Content` component.
 */

const Content = () => {
  const { t } = useTranslation('schedule');
  const { step } = useSchedule();
  const isMobile = useBreakpoint(0, 'md');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const prevStepRef = useRef<number>(step);

  useEffect(() => {
    if (isMobile && prevStepRef.current !== step && headingRef.current) {
      headingRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    prevStepRef.current = step;
  }, [isMobile, step]);

  return (
    <AnimatePresence mode={'wait'}>
      {[1, 2, 3].includes(step) && (
        <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
          <AnimationController staggerMobile>
            <StyledHeading
              asDescription={'p'}
              asTitle={'h1'}
              description={t(`steps.${step}.description`)}
              descriptionTheme={'slate300'}
              icon={scheduleSvg}
              ref={headingRef}
              staggerMobile
              title={t(`steps.${step}.title`)}
              titleSize={28}
              titleTheme={'slate50'}
            />

            <FadeInUp staggerMobile>
              <StepsGrid>
                <StepsProgress />

                <Steps />
              </StepsGrid>
            </FadeInUp>
          </AnimationController>
        </motion.div>
      )}

      {step === 4 && <ErrorMessage type={'unavailable'} />}

      {step === 5 && (
        <SuccessMessage>
          <ServicePayment />
        </SuccessMessage>
      )}

      {step === 6 && <ErrorMessage type={'error'} />}

      {step === 7 && <ErrorMessage type={'paymentUnavailable'} />}
    </AnimatePresence>
  );
};

/**
 * `ScheduleContent` component.
 */

const ScheduleContent = () => {
  const router = useRouter();
  const { t } = useTranslation('schedule');
  const { previousPath } = useHistory();
  const { isSantanderFlow, step } = useSchedule();

  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const allowNavigationRef = useRef(false);

  // Check if we should block navigation (only during active flow steps 1-3)
  const shouldBlockNavigation = [1, 2, 3].includes(step);

  // Handle browser beforeunload (refresh, close tab, close browser)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlockNavigation) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockNavigation]);

  // Handle Next.js router navigation
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (!allowNavigationRef.current && shouldBlockNavigation) {
        setShowExitModal(true);
        setPendingNavigation(url);
        router.events.emit('routeChangeError');
        throw new Error('Route change aborted by user confirmation.');
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router, shouldBlockNavigation]);

  const handleClose = useCallback(() => {
    if (shouldBlockNavigation) {
      setShowExitModal(true);

      return;
    }

    if (previousPath) {
      router.back();

      return;
    }

    router.push(isSantanderFlow ? routes.landingPage : '/');
  }, [isSantanderFlow, previousPath, router, shouldBlockNavigation]);

  const handleExitConfirm = useCallback(() => {
    allowNavigationRef.current = true;
    setShowExitModal(false);

    if (pendingNavigation) {
      router.push(pendingNavigation);
    } else if (previousPath) {
      router.back();
    } else {
      router.push(isSantanderFlow ? routes.landingPage : '/');
    }
  }, [isSantanderFlow, pendingNavigation, previousPath, router]);

  const handleExitCancel = useCallback(() => {
    setShowExitModal(false);
    setPendingNavigation(null);
  }, []);

  return (
    <>
      <Section>
        <Container>
          <Grid>
            <SubGrid>
              <Nav>
                <Svg aria-label={'Logo'} icon={logoSvg} size={'164px'} />

                <Button
                  aria-label={t('common:actions.close')}
                  colorTheme={'ghost'}
                  icon={closeSvg}
                  onClick={() => handleClose()}
                  reverse
                  size={'medium'}
                >
                  {t('common:actions.close')}
                </Button>
              </Nav>

              <Content />
            </SubGrid>
          </Grid>
        </Container>
      </Section>

      <ExitConfirmationModal
        handleContinue={handleExitCancel}
        handleExit={handleExitConfirm}
        isOpen={showExitModal}
        onClose={handleExitCancel}
      />
    </>
  );
};

/**
 * `Schedule` page.
 */

const Schedule: NextPage<Props> = ({ data, districts }) => {
  return (
    <>
      <SEO {...normalizePrismicSEO(data)} />

      <ScheduleProvider districts={districts?.data} status={districts?.status}>
        <ScheduleContent />
      </ScheduleProvider>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = withSettings<any>(async ({ locale, previewData }) => {
  const client = createPrimsicClient({ previewData });
  const { data } = await client.getSingle('schedule');
  const districts = await getRadarDistricts();

  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'schedule'])),
      data,
      districts,
      hideLayout: true
    },
    revalidate: 60
  };
});

/**
 * Export `Schedule` page.
 */

export default Schedule;
